import React from 'react';
import { Link } from 'react-router-dom';
import { FaBook, FaRocket, FaArrowRight, FaLightbulb, FaFileAlt } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const PkmFeatures = () => {
  usePageTitle('PKM Corner');
  const { t } = useLanguage();

  const features = [
    {
      icon: <FaBook className="text-3xl" />,
      gradient: 'from-amber-400 to-orange-500',
      cardBg: 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20',
      borderColor: 'border-amber-200 dark:border-amber-700/40',
      title: t('pkmFeatures.bankTitle'),
      desc: t('pkmFeatures.bankDesc'),
      to: '/pkm-bank',
      cta: t('pkmFeatures.bankCta'),
      badges: [
        { label: t('pkmFeatures.badgeTemplates'), color: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300' },
        { label: 'Google Drive', color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' },
      ],
    },
    {
      icon: <FaRocket className="text-3xl" />,
      gradient: 'from-violet-500 to-indigo-600',
      cardBg: 'bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20',
      borderColor: 'border-violet-200 dark:border-violet-700/40',
      title: t('pkmFeatures.builderTitle'),
      desc: t('pkmFeatures.builderDesc'),
      to: '/pkm-builder',
      cta: t('pkmFeatures.builderCta'),
      badges: [
        { label: 'AI-Powered', color: 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300' },
        { label: t('pkmFeatures.badgeTeam'), color: 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300' },
      ],
    },
  ];

  return (
    <div className="font-inter bg-gray-50 dark:bg-dark-900 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 via-primary-700 to-indigo-800 py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-amber-300 text-sm font-semibold px-5 py-2 rounded-full mb-6 border border-white/10">
            <FaLightbulb /> PKM Corner
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">{t('pkmFeatures.title')}</h1>
          <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto">{t('pkmFeatures.subtitle')}</p>
        </div>
      </div>

      {/* Cards */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((f, idx) => (
            <Link
              key={idx}
              to={f.to}
              className={`group relative ${f.cardBg} border ${f.borderColor} rounded-2xl p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden`}
            >
              {/* Icon */}
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${f.gradient} flex items-center justify-center text-white mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                {f.icon}
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-3">
                {f.badges.map((b, i) => (
                  <span key={i} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${b.color}`}>{b.label}</span>
                ))}
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">{f.title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-5">{f.desc}</p>

              {/* CTA */}
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 dark:text-primary-400 group-hover:gap-3 transition-all">
                {f.cta} <FaArrowRight className="text-xs" />
              </div>

              {/* Decorative */}
              <div className={`absolute -bottom-8 -right-8 w-32 h-32 bg-gradient-to-br ${f.gradient} opacity-[0.06] rounded-full blur-2xl group-hover:opacity-[0.12] transition-opacity duration-300`}></div>
            </Link>
          ))}
        </div>

        {/* Info section */}
        <div className="mt-12 bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shrink-0">
              <FaFileAlt className="text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{t('pkmFeatures.infoTitle')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{t('pkmFeatures.infoDesc')}</p>
            </div>
          </div>

          {/* PKM Types quick overview */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { code: 'PKM-R', name: 'Riset' },
              { code: 'PKM-K', name: 'Kewirausahaan' },
              { code: 'PKM-PM', name: 'Pengabdian' },
              { code: 'PKM-PI', name: 'Penerapan Iptek' },
              { code: 'PKM-KC', name: 'Karsa Cipta' },
              { code: 'PKM-RE', name: 'Riset Eksakta' },
              { code: 'PKM-RSH', name: 'Riset Sosial Humaniora' },
              { code: 'PKM-GFT', name: 'Gagasan Futuristik Tertulis' },
              { code: 'PKM-VGK', name: 'Video Gagasan Konstruktif' },
              { code: 'PKM-AI', name: 'Artikel Ilmiah' },
            ].map(pkm => (
              <div
                key={pkm.code}
                className="bg-gray-50 dark:bg-dark-700 rounded-xl px-3 py-2.5 text-center border border-gray-100 dark:border-dark-600"
              >
                <p className="text-xs font-bold text-primary-600 dark:text-primary-400">{pkm.code}</p>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">{pkm.name}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PkmFeatures;
